import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  SetStateAction
} from "react";
import { useLocation } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import {
  serializeArrayParam,
  deserializeArrayParam,
  serializeObjectArrayParam,
  deserializeObjectArrayParam
} from "shared/utils/search-params-serialization";
import {
  CreatedWithinOption,
  LeadsFilters,
  ShowingWithinOption
} from "../types";
import { isApprovalLikelihoodBand } from "api/approval-likelihood";
import { isPrescreeningResult } from "shared/app/PrescreeningResult";
import {
  BASE_DEFAULT_URL_PARAMS,
  BASE_URL_PARAM_KEYS,
  DEFAULT_PER_PAGE,
  DEFAULT_SORTING,
  DEFAULT_URL_PARAMS,
  LeadSortColumnId,
  LeadView,
  SESSION_STORAGE_KEY,
  URL_PARAM_KEYS
} from "../constants";

const DATE_PARAM_REGEX = /^\d{4}-\d{2}-\d{2}$/;

interface UseLeadsQueryParamsReturn {
  filters: LeadsFilters | undefined;
  searchParams: Record<string, string>;
  sorting: SortingState;
  assigneeIds: number[] | null;
  view: LeadView;
  isInitialized: boolean;
  setFilters: (
    filtersOrUpdater: SetStateAction<LeadsFilters | undefined>
  ) => void;
  setSearchParams: (params: Record<string, string>) => void;
  setSorting: (sorting: SortingState) => void;
  setAssigneeIds: (assigneeIds: number[] | null) => void;
  setView: (view: LeadView) => void;
  applyState: (next: LeadsQueryParamsSnapshot) => void;
}

interface LeadsQueryParamsSnapshot {
  filters: LeadsFilters | undefined;
  search: string;
  starred: boolean;
  sorting: SortingState;
  assigneeIds: number[] | null;
  view: LeadView;
}

const parseDateParam = (value: string): string | undefined => {
  if (!DATE_PARAM_REGEX.test(value)) return undefined;
  // Reject regex-valid but impossible dates (e.g. 2026-13-40) — they would
  // throw RangeError in the queryFn's toISOString call
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : value;
};

const parseCreatedWithinParam = (
  value: string
): CreatedWithinOption | undefined =>
  (Object.values(CreatedWithinOption) as string[]).includes(value)
    ? (value as CreatedWithinOption)
    : undefined;

const parseShowingWithinParam = (
  value: string
): ShowingWithinOption | undefined =>
  (Object.values(ShowingWithinOption) as string[]).includes(value)
    ? (value as ShowingWithinOption)
    : undefined;

const assignIfPresent = <K extends keyof LeadsFilters>(
  target: LeadsFilters,
  key: K,
  value: LeadsFilters[K] | undefined
) => {
  if (Array.isArray(value) && value.length) target[key] = value;
};

const parseFiltersFromParams = (
  params: Record<string, string>
): LeadsFilters | undefined => {
  const properties = deserializeObjectArrayParam(params.properties);
  const units = deserializeObjectArrayParam(params.units);
  const stages = deserializeObjectArrayParam(params.stages);
  const leadSources = deserializeObjectArrayParam(params.lead_sources);
  const leadTypes = deserializeArrayParam(params.lead_types);
  const activityTypes = deserializeArrayParam(params.activity_types);
  const approvalLikelihoodBands = deserializeArrayParam(
    params.approval_likelihood_bands
  )?.filter(isApprovalLikelihoodBand);
  const prescreeningResults = deserializeArrayParam(
    params.prescreening_results
  )?.filter(isPrescreeningResult);
  const onWaitlist = params.from_waitlist === "true";
  const createdWithin = parseCreatedWithinParam(params.created_within);
  const createdAfter = parseDateParam(params.created_after);
  const createdBefore = parseDateParam(params.created_before);
  const showingWithin = parseShowingWithinParam(params.showing_within);
  const showingAfter = parseDateParam(params.showing_after);
  const showingBefore = parseDateParam(params.showing_before);

  const result: LeadsFilters = {};

  if (properties) result.properties = properties;
  if (units) result.units = units;
  if (stages) result.stages = stages;
  if (leadSources) result.leadSources = leadSources;
  if (leadTypes) result.leadTypes = leadTypes;
  if (activityTypes) result.activityTypes = activityTypes;
  assignIfPresent(result, "approvalLikelihoodBands", approvalLikelihoodBands);
  assignIfPresent(result, "prescreeningResults", prescreeningResults);
  if (onWaitlist) result.onWaitlist = true;
  if (createdWithin) result.createdWithin = createdWithin;
  if (createdAfter) result.createdAfter = createdAfter;
  if (createdBefore) result.createdBefore = createdBefore;
  if (showingWithin) result.showingWithin = showingWithin;
  if (showingAfter) result.showingAfter = showingAfter;
  if (showingBefore) result.showingBefore = showingBefore;

  return Object.keys(result).length > 0 ? result : undefined;
};

const parseSortingFromParams = (
  params: Record<string, string>
): SortingState => {
  const isValidSortColumn = (
    Object.values(LeadSortColumnId) as string[]
  ).includes(params.sort_by);
  if (!isValidSortColumn) return DEFAULT_SORTING;
  return [{ id: params.sort_by, desc: params.sort_dir === "desc" }];
};

const serializeCreatedWithin = (
  createdWithin: CreatedWithinOption | undefined
): string => {
  if (!createdWithin || createdWithin === CreatedWithinOption.None) return "";
  return createdWithin;
};

const serializeShowingWithin = (
  showingWithin: ShowingWithinOption | undefined
): string => {
  if (!showingWithin || showingWithin === ShowingWithinOption.None) return "";
  return showingWithin;
};

const serializeSortDir = (sort: SortingState[number] | undefined): string => {
  if (!sort) return "";
  return sort.desc ? "desc" : "asc";
};

export const useLeadsQueryParams = ({
  persistParams = false
}: { persistParams?: boolean } = {}): UseLeadsQueryParamsReturn => {
  const location = useLocation();

  // Embedded leads tables (contact/property/unit pages) must not share the
  // global /leads sessionStorage entry, so they skip restore/save entirely.
  // Synchronously check if URL already has params - if so, we're initialized
  const [isInitialized, setIsInitialized] = useState(() => {
    if (!persistParams) return true;
    const currentParams = new URLSearchParams(window.location.search);
    return URL_PARAM_KEYS.some((key) => currentParams.get(key));
  });

  // Embedded tables bind only the base keys to the URL: the filter keys
  // ("properties", "assignee_ids", "sort_by", ...) are also managed by
  // useApplicationQueryParams, and both tables render on the same tabbed
  // routes — binding them there cross-contaminates the two tables. Filters,
  // sorting and assignees stay in local state instead (pre-existing behavior).
  const [urlParams, setUrlParams] = useSearchParamsState(
    persistParams ? DEFAULT_URL_PARAMS : BASE_DEFAULT_URL_PARAMS,
    persistParams ? [...URL_PARAM_KEYS] : [...BASE_URL_PARAM_KEYS],
    { excludeEmptyValues: true }
  );

  const [localFilters, setLocalFilters] = useState<LeadsFilters>();
  const [localSorting, setLocalSorting] =
    useState<SortingState>(DEFAULT_SORTING);
  const [localAssigneeIds, setLocalAssigneeIds] = useState<number[] | null>(
    null
  );

  // Restore from sessionStorage if URL has no params (on initial mount only)
  useEffect(() => {
    if (isInitialized) return;

    const savedQueryString = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (savedQueryString) {
      const restoredParams = new URLSearchParams(savedQueryString);
      const newParams = { ...DEFAULT_URL_PARAMS };
      URL_PARAM_KEYS.forEach((key) => {
        const value = restoredParams.get(key);
        if (value) newParams[key] = value;
      });
      setUrlParams(newParams);
    }

    setIsInitialized(true);
  }, []);

  // Mirror the current params into sessionStorage on every change (not in an
  // effect cleanup, which lags one transition behind and would resurrect
  // cleared filters after a hard reload)
  useEffect(() => {
    if (!persistParams || !isInitialized) return;

    if (location.search)
      sessionStorage.setItem(SESSION_STORAGE_KEY, location.search.slice(1)); // Remove leading '?'
    else sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }, [persistParams, isInitialized, location.search]);

  const urlFilters = useMemo(
    () => parseFiltersFromParams(urlParams),
    // Depend on the specific keys so unrelated param changes (page, search)
    // don't re-mint the filters identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      urlParams.properties,
      urlParams.units,
      urlParams.stages,
      urlParams.lead_sources,
      urlParams.lead_types,
      urlParams.activity_types,
      urlParams.approval_likelihood_bands,
      urlParams.prescreening_results,
      urlParams.from_waitlist,
      urlParams.created_within,
      urlParams.created_after,
      urlParams.created_before,
      urlParams.showing_within,
      urlParams.showing_after,
      urlParams.showing_before
    ]
  );

  const urlSorting = useMemo(
    () => parseSortingFromParams(urlParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [urlParams.sort_by, urlParams.sort_dir]
  );

  const urlAssigneeIds = useMemo(() => {
    const parsed = deserializeArrayParam(urlParams.assignee_ids)
      ?.map(Number)
      .filter(Number.isFinite);
    return parsed?.length ? parsed : null;
  }, [urlParams.assignee_ids]);

  const filters = persistParams ? urlFilters : localFilters;
  const sorting = persistParams ? urlSorting : localSorting;
  const assigneeIds = persistParams ? urlAssigneeIds : localAssigneeIds;

  // Pipeline view only exists on the main /leads page; embedded tables stay list-only.
  const view =
    persistParams && urlParams.view === LeadView.Pipeline
      ? LeadView.Pipeline
      : LeadView.List;

  // Maintain backward compatibility with existing searchParams structure
  const searchParams = useMemo(
    () => ({
      search: urlParams.search || "",
      page: urlParams.page || "1",
      per_page: urlParams.per_page || DEFAULT_PER_PAGE,
      starred: urlParams.starred || ""
    }),
    [urlParams.search, urlParams.page, urlParams.per_page, urlParams.starred]
  );

  const setFilters = useCallback(
    (filtersOrUpdater: SetStateAction<LeadsFilters | undefined>) => {
      if (!persistParams) {
        setLocalFilters(filtersOrUpdater);
        setUrlParams({ ...urlParams, page: "" });
        return;
      }

      const currentFilters = parseFiltersFromParams(urlParams);
      const newFilters =
        typeof filtersOrUpdater === "function"
          ? filtersOrUpdater(currentFilters)
          : filtersOrUpdater;

      setUrlParams({
        ...urlParams,
        properties: serializeObjectArrayParam(newFilters?.properties),
        units: serializeObjectArrayParam(newFilters?.units),
        stages: serializeObjectArrayParam(newFilters?.stages),
        lead_sources: serializeObjectArrayParam(newFilters?.leadSources),
        lead_types: serializeArrayParam(newFilters?.leadTypes),
        activity_types: serializeArrayParam(newFilters?.activityTypes),
        approval_likelihood_bands: serializeArrayParam(
          newFilters?.approvalLikelihoodBands
        ),
        prescreening_results: serializeArrayParam(
          newFilters?.prescreeningResults
        ),
        from_waitlist: newFilters?.onWaitlist ? "true" : "",
        created_within: serializeCreatedWithin(newFilters?.createdWithin),
        created_after: newFilters?.createdAfter || "",
        created_before: newFilters?.createdBefore || "",
        showing_within: serializeShowingWithin(newFilters?.showingWithin),
        showing_after: newFilters?.showingAfter || "",
        showing_before: newFilters?.showingBefore || "",
        page: ""
      });
    },
    [persistParams, urlParams, setUrlParams]
  );

  const setSearchParamsWrapper = useCallback(
    (newParams: Record<string, string>) => {
      const updates: Record<string, string> = { ...urlParams };

      if ("search" in newParams) updates.search = newParams.search || "";

      if ("page" in newParams)
        updates.page = newParams.page === "1" ? "" : newParams.page || "";

      if ("per_page" in newParams)
        updates.per_page =
          newParams.per_page === DEFAULT_PER_PAGE
            ? ""
            : newParams.per_page || "";

      if ("starred" in newParams) updates.starred = newParams.starred || "";

      setUrlParams(updates);
    },
    [urlParams, setUrlParams]
  );

  const setSorting = useCallback(
    (newSorting: SortingState) => {
      if (!persistParams) {
        setLocalSorting(newSorting);
        setUrlParams({ ...urlParams, page: "" });
        return;
      }

      const sort = newSorting[0];
      setUrlParams({
        ...urlParams,
        sort_by: sort?.id || "",
        sort_dir: serializeSortDir(sort),
        page: ""
      });
    },
    [persistParams, urlParams, setUrlParams]
  );

  const setAssigneeIds = useCallback(
    (newAssigneeIds: number[] | null) => {
      if (!persistParams) {
        setLocalAssigneeIds(newAssigneeIds);
        setUrlParams({ ...urlParams, page: "" });
        return;
      }

      setUrlParams({
        ...urlParams,
        assignee_ids: newAssigneeIds
          ? serializeArrayParam(newAssigneeIds.map(String))
          : "",
        page: ""
      });
    },
    [persistParams, urlParams, setUrlParams]
  );

  // Atomically rehydrate the whole filter/search/sorting/assignee state in a
  // single update. Applying a saved view must not chain setFilters +
  // setSearchParams + setSorting + setAssigneeIds — each derives its next URL
  // params from the same stale snapshot, so the last call would clobber the
  // others. This writes every managed key in one go.
  const applyState = useCallback(
    (next: LeadsQueryParamsSnapshot) => {
      if (!persistParams) {
        setLocalFilters(next.filters);
        setLocalSorting(next.sorting);
        setLocalAssigneeIds(next.assigneeIds);
        setUrlParams({
          ...urlParams,
          search: next.search,
          starred: next.starred ? "true" : "",
          page: ""
        });
        return;
      }

      const sort = next.sorting[0];
      setUrlParams({
        ...urlParams,
        search: next.search,
        starred: next.starred ? "true" : "",
        properties: serializeObjectArrayParam(next.filters?.properties),
        units: serializeObjectArrayParam(next.filters?.units),
        stages: serializeObjectArrayParam(next.filters?.stages),
        lead_sources: serializeObjectArrayParam(next.filters?.leadSources),
        activity_types: serializeArrayParam(next.filters?.activityTypes),
        lead_types: serializeArrayParam(next.filters?.leadTypes),
        approval_likelihood_bands: serializeArrayParam(
          next.filters?.approvalLikelihoodBands
        ),
        prescreening_results: serializeArrayParam(
          next.filters?.prescreeningResults
        ),
        from_waitlist: next.filters?.onWaitlist ? "true" : "",
        created_within: serializeCreatedWithin(next.filters?.createdWithin),
        created_after: next.filters?.createdAfter || "",
        created_before: next.filters?.createdBefore || "",
        showing_within: serializeShowingWithin(next.filters?.showingWithin),
        showing_after: next.filters?.showingAfter || "",
        showing_before: next.filters?.showingBefore || "",
        assignee_ids: next.assigneeIds?.length
          ? serializeArrayParam(next.assigneeIds.map(String))
          : "",
        sort_by: sort?.id || "",
        sort_dir: serializeSortDir(sort),
        view: next.view === LeadView.Pipeline ? LeadView.Pipeline : "",
        page: ""
      });
    },
    [persistParams, urlParams, setUrlParams]
  );

  const setView = useCallback(
    (newView: LeadView) => {
      if (!persistParams) return;
      setUrlParams({
        ...urlParams,
        view: newView === LeadView.Pipeline ? LeadView.Pipeline : "",
        page: ""
      });
    },
    [persistParams, urlParams, setUrlParams]
  );

  return useMemo(
    (): UseLeadsQueryParamsReturn => ({
      filters,
      searchParams,
      sorting,
      assigneeIds,
      view,
      isInitialized,
      setFilters,
      setSearchParams: setSearchParamsWrapper,
      setSorting,
      setAssigneeIds,
      setView,
      applyState
    }),
    [
      filters,
      searchParams,
      sorting,
      assigneeIds,
      view,
      isInitialized,
      setFilters,
      setSearchParamsWrapper,
      setSorting,
      setAssigneeIds,
      setView,
      applyState
    ]
  );
};
